import { ReactComponent as Close16 } from '../../assets/svg/Clos16.svg'
import { BTN_TXT, PLACEHOLDERS, VALIDATION_MESSAGES } from '../../constants'
import { ReactComponent as Search16 } from '../../assets/svg/Search16.svg'
import { Input } from '../../components/Input'
import { Button, BUTTON_TYPES } from '../../components/Button'
import { ReactComponent as Plus16 } from '../../assets/svg/Plus16.svg'
import { Table } from '../../components/Table'
import { useCallback, useEffect, useRef, useState } from 'react'
import { DEFAULT_PAGE } from '../../components/Table/constants'
import axios, { CancelTokenSource } from 'axios'
import { useDispatch, useSelector } from 'react-redux'
import { useDebounce } from '../../hooks/hooks'
import { columns } from './constants'
import { useProductRequestsContext } from '../../pages/ProductRequests/ProductRequestsProvider'
import { setLoading } from '../../redux/store/common/slice'
import {
  TProductRequestsList,
  TProductRequestsListFilters,
  TProductRequestsListFiltersFunc,
  TProductRequestsListParams,
  TProductRequestsTableRequestData
} from './api'
import { isTableHasParams } from '../../helper/common'
import { notification } from 'antd'
import { routes } from '../../router'
import useRouter from '../../hooks/useRouter'
import { getUser } from '../../redux/store/user/getters'
import { getPrsList } from '../../pages/ProductRequests/api'
import StartPrs from './StartPrs'
import './style.scss'

export const ProductRequestsTable = () => {
  const { state } = useProductRequestsContext()
  const { push } = useRouter()
  const user = useSelector(getUser)
  const { isVCS, isVendor } = state
  const [tableData, setTableData] = useState<TProductRequestsList[]>([])
  const [searchValue, setSearchValue] = useState('')
  const [sortParams, setSortParams] =
    useState<TProductRequestsTableRequestData>()
  const [firstLoad, setFirstLoad] = useState(true)
  const [filters, setFilters] = useState<TProductRequestsListFilters>({})
  const [totalNumber, setTotalNumber] = useState(0)
  const [pageInfo, setPageInfo] = useState({ ...DEFAULT_PAGE })
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const tableDataTokenRef = useRef<CancelTokenSource>()
  const dispatch = useDispatch()
  const debouncedSearch = useDebounce(searchValue, 500)

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(
      () => {
        getTableData({ ...sortParams, ...filters, page: { ...DEFAULT_PAGE } })
        setPageInfo({ ...DEFAULT_PAGE })
      },
      firstLoad ? 0 : 500
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch])

  useEffect(() => {
    return () => {
      if (!!tableDataTokenRef.current) {
        tableDataTokenRef.current.cancel()
      }
    }
  }, [])
  const createPrs = () => {
    if (!user.contract_categories?.length) {
      notification.error({ message: VALIDATION_MESSAGES.SM0017_1 })
      return
    }
    push(`${routes.productRequests}/new`)
  }

  const getTableData = ({
    sortField,
    sortOrder,
    filters,
    page
  }: TProductRequestsTableRequestData) => {
    dispatch(setLoading(true))
    if (!!tableDataTokenRef.current) {
      tableDataTokenRef.current.cancel()
    }
    const params: TProductRequestsListParams = { ...filters }
    if (searchValue) {
      params.search = searchValue
    }
    if (sortField) {
      if (sortOrder) {
        params.ordering = `${sortOrder === 'descend' ? '-' : ''}${sortField}`
      }
      setSortParams({
        sortField,
        sortOrder
      })
    }
    const dataPage = page ? page : pageInfo
    params.limit = dataPage.pageSize
    params.offset = (dataPage.pageNumber - 1) * dataPage.pageSize
    tableDataTokenRef.current = axios.CancelToken.source()
    getPrsList({
      cancelToken: tableDataTokenRef.current?.token,
      params
    })
      .then((resp) => {
        if (!resp.data?.results) {
          return
        }
        setTableData(
          (resp.data.results as TProductRequestsList[]).map((i) => ({
            ...i,
            key: i.name
          }))
        )
        setTotalNumber(resp.data.count)
        if (firstLoad) {
          setFirstLoad(false)
        }
      })
      .finally(() => dispatch(setLoading(false)))
  }

  const onSearchChangeValue = (e) => {
    setSearchValue(e.target.value)
  }

  const handleTableChange = (pagination, _filters, sorter) => {
    const page = {
      pageNumber: pagination.current,
      pageSize: pagination.pageSize
    }
    getTableData({
      sortField: sorter.field,
      sortOrder: sorter.order,
      filters,
      page
    })
    setPageInfo(page)
  }

  const handleChangePageSize = (pageSize) => {
    const page = {
      pageNumber: 1,
      pageSize
    }
    getTableData({
      ...sortParams,
      filters,
      page
    })
    setPageInfo(page)
  }

  const applyFilters: TProductRequestsListFiltersFunc = (
    field,
    appliedFilters
  ) => {
    const newFilters = { ...filters, [field]: appliedFilters }
    const newPageInfo = { ...pageInfo, pageNumber: 1 }
    setFilters(newFilters)
    setPageInfo(newPageInfo)
    getTableData({ ...sortParams, filters: newFilters, page: newPageInfo })
  }

  const handleSelectedRow = useCallback(
    (uuid: string) => {
      push(`${routes.productRequests}/${uuid}`)
    },
    [push]
  )

  return (
    <div>
      <div className="table-wrapper__header">
        <Input
          className="allow-clear"
          propsInput={{
            allowClear: { clearIcon: <Close16 /> },
            placeholder: isVendor
              ? PLACEHOLDERS.SEARCH_BY_PRS_COMMUNITY_NAME
              : PLACEHOLDERS.SEARCH_BY_PRS_VENDOR_NAME,
            prefix: <Search16 />,
            value: searchValue,
            onChange: onSearchChangeValue
          }}
        />
        {isVCS && (
          <div className="table-wrapper__header-buttons">
            <Button
              type={BUTTON_TYPES.PRIMARY}
              icon={<Plus16 />}
              upperCase
              onClick={createPrs}
            >
              {BTN_TXT.CREATE_PRS}
            </Button>
          </div>
        )}
      </div>
      {!firstLoad && (
        <Table<TProductRequestsList>
          dataSource={tableData}
          columns={columns({
            applyFilters,
            filters: filters,
            isVendor,
            handleSelectedRow
          })}
          className="product-requests-table"
          onChange={handleTableChange}
          onChangePage={handleChangePageSize}
          pageSize={pageInfo.pageSize}
          pagination={{
            pageSize: pageInfo.pageSize,
            current: pageInfo.pageNumber,
            total: totalNumber
          }}
          subtitle={`No active requests from Vendors`}
          hasSearchOrFilters={isTableHasParams<TProductRequestsListFilters>(
            debouncedSearch,
            filters
          )}
          rowKey={(row) => row?.uuid}
        />
      )}
      <StartPrs />
    </div>
  )
}
